import { useState, useEffect, useCallback, createContext, useContext } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Mail, Copy, Check, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const EMAIL = "hei@clientflow.no";
const ContactCtx = createContext({ open: () => {} });

/**
 * Wraps the app with a single shared contact modal.
 * Any descendant can call `useContact().open({ source, defaultMessage })` to
 * trigger the modal — no per-page wiring needed.
 */
export function ContactProvider({ children }) {
  const [state, setState] = useState({
    open: false, source: null, defaultMessage: "",
    defaultName: "", defaultEmail: "", defaultCompany: "", defaultInterest: "",
  });

  const open = useCallback((opts = {}) => {
    setState({
      open: true,
      source: opts.source || "unknown",
      defaultMessage: opts.defaultMessage || "",
      // Optional prefills — used when the user is already logged in so they
      // don't have to retype their own name/email/company.
      defaultName: opts.defaultName || "",
      defaultEmail: opts.defaultEmail || "",
      defaultCompany: opts.defaultCompany || "",
      defaultInterest: opts.defaultInterest || "",
    });
  }, []);
  const close = useCallback(() => setState((s) => ({ ...s, open: false })), []);

  return (
    <ContactCtx.Provider value={{ open }}>
      {children}
      <ContactModal
        open={state.open}
        onOpenChange={(v) => (v ? null : close())}
        source={state.source}
        defaultMessage={state.defaultMessage}
        defaultName={state.defaultName}
        defaultEmail={state.defaultEmail}
        defaultCompany={state.defaultCompany}
        defaultInterest={state.defaultInterest}
      />
    </ContactCtx.Provider>
  );
}

export function useContact() {
  return useContext(ContactCtx);
}

function ContactModal({
  open, onOpenChange, source, defaultMessage,
  defaultName = "", defaultEmail = "", defaultCompany = "", defaultInterest = "",
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [interest, setInterest] = useState("");
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [copied, setCopied] = useState(false);

  // Treat the shared community demo email as "no prefill" — we never want
  // visitor messages routed back to the generic demo inbox. Force them to
  // type their own address instead.
  const isSharedDemoEmail = defaultEmail === "demo@clientflow.no";

  // Reset on open — honour any prefills passed by the caller (e.g. shared
  // demo notice fills in the logged-in user's name/email/company).
  useEffect(() => {
    if (open) {
      setName(defaultName || "");
      setEmail(isSharedDemoEmail ? "" : (defaultEmail || ""));
      setCompany(defaultCompany || "");
      setInterest(defaultInterest || "");
      setMessage(defaultMessage || "");
      setErrors({}); setSuccess(false); setCopied(false);
    }
  }, [open, defaultMessage, defaultName, defaultEmail, defaultCompany, defaultInterest, isSharedDemoEmail]);

  const validate = () => {
    const e = {};
    if (!name.trim() || name.trim().length < 2) e.name = "Skriv inn navn (min 2 tegn)";
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = "Ugyldig e-post";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await api.post("/public/contact", {
        name: name.trim(),
        email: email.trim(),
        company: company.trim() || null,
        message: message.trim() || null,
        source: source || null,
        interest: interest || null,
      });
      setSuccess(true);
      toast.success("Takk! Vi tar kontakt snart.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error("Sjekk feltene og prøv igjen.");
      } else {
        toast.error(typeof detail === "string" ? detail : "Kunne ikke sende. Prøv igjen.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const copyEmail = async () => {
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(EMAIL);
      else {
        const ta = document.createElement("textarea");
        ta.value = EMAIL; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
      }
      setCopied(true);
      toast.success("E-post kopiert", { description: EMAIL });
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Klarte ikke å kopiere");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md rounded-2xl"
        data-testid="contact-modal"
      >
        {success ? (
          <div className="text-center py-4" data-testid="contact-success">
            <div className="inline-flex w-14 h-14 rounded-2xl bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 items-center justify-center mb-3">
              <Check className="w-6 h-6" strokeWidth={2} />
            </div>
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">Takk! Vi tar kontakt snart.</DialogTitle>
              <DialogDescription className="mt-2">
                Vi svarer som regel innen 24 timer. Sjekk gjerne søppelposten hvis du ikke hører fra oss.
              </DialogDescription>
            </DialogHeader>
            <Button onClick={() => onOpenChange(false)} className="mt-6 rounded-xl w-full" data-testid="contact-close-btn">
              Lukk
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">Kontakt oss</DialogTitle>
              <DialogDescription>
                Fyll ut skjemaet så tar vi kontakt for en personlig demo.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="mt-2 space-y-4" noValidate>
              <div>
                <Label htmlFor="contact-name">Navn</Label>
                <Input
                  id="contact-name" value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="Ola Nordmann" className="rounded-xl mt-1.5" autoComplete="name"
                  data-testid="contact-name-input"
                />
                {errors.name && <p className="text-xs text-destructive mt-1">{errors.name}</p>}
              </div>
              <div>
                <Label htmlFor="contact-email">E-post</Label>
                <Input
                  id="contact-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="ola@bedrift.no" className="rounded-xl mt-1.5" autoComplete="email"
                  required={isSharedDemoEmail}
                  data-testid="contact-email-input"
                />
                {isSharedDemoEmail && (
                  <p
                    className="text-xs text-muted-foreground mt-1.5"
                    data-testid="contact-email-shared-demo-hint"
                  >
                    Skriv inn din egen e-postadresse hvis du ønsker svar.
                  </p>
                )}
                {errors.email && <p className="text-xs text-destructive mt-1">{errors.email}</p>}
              </div>
              <div>
                <Label htmlFor="contact-company">Bedriftsnavn <span className="text-muted-foreground font-normal">(valgfritt)</span></Label>
                <Input
                  id="contact-company" value={company} onChange={(e) => setCompany(e.target.value)}
                  placeholder="Nordlys Frisør" className="rounded-xl mt-1.5" autoComplete="organization"
                  data-testid="contact-company-input"
                />
              </div>
              <div>
                <Label htmlFor="contact-interest">Hva gjelder henvendelsen? <span className="text-muted-foreground font-normal">(valgfritt)</span></Label>
                <Select value={interest} onValueChange={setInterest}>
                  <SelectTrigger
                    id="contact-interest"
                    className="rounded-xl mt-1.5"
                    data-testid="contact-interest-select"
                  >
                    <SelectValue placeholder="Velg pakke eller spørsmål" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="demo" data-testid="contact-interest-demo">Demo</SelectItem>
                    <SelectItem value="basic" data-testid="contact-interest-basic">Basic</SelectItem>
                    <SelectItem value="pro" data-testid="contact-interest-pro">Pro</SelectItem>
                    <SelectItem value="premium" data-testid="contact-interest-premium">Premium</SelectItem>
                    <SelectItem value="annet" data-testid="contact-interest-annet">Annet spørsmål</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="contact-message">Melding <span className="text-muted-foreground font-normal">(valgfritt)</span></Label>
                <Textarea
                  id="contact-message" value={message} onChange={(e) => setMessage(e.target.value)}
                  placeholder="Fortell oss kort hva du driver med…" rows={3}
                  className="rounded-xl mt-1.5" data-testid="contact-message-input"
                />
              </div>
              <Button
                type="submit" className="w-full rounded-xl bg-primary hover:bg-primary/90"
                disabled={submitting} data-testid="contact-submit-btn"
              >
                {submitting ? (
                  "Sender..."
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" strokeWidth={1.75} />
                    Send henvendelse
                  </>
                )}
              </Button>
            </form>
            <div className="mt-5 pt-5 border-t border-border text-xs text-muted-foreground">
              <p className="mb-2">Foretrekker du e-post direkte?</p>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-foreground">{EMAIL}</span>
                <button
                  type="button"
                  onClick={copyEmail}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md hover:bg-accent active:scale-95 transition-all"
                  data-testid="contact-copy-email"
                >
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? "Kopiert" : "Kopier e-post"}
                </button>
                <a
                  href={`mailto:${EMAIL}?subject=Tilgang%20til%20ClientFlow`}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md hover:bg-accent transition-colors"
                  data-testid="contact-mailto-link"
                >
                  <Mail className="w-3.5 h-3.5" />
                  Åpne e-postprogram
                </a>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Tiny helper for buttons that should open the contact modal.
 * Renders its children inside a normal button-shaped element and triggers
 * the shared modal on click. Pass `source` to track lead origin in the DB.
 *
 * Example: <ContactCta source="hero" asChild><Button>Book demo</Button></ContactCta>
 */
export function ContactCta({ children, source, defaultMessage, asChild = false, ...rest }) {
  const { open } = useContact();
  const onClick = (e) => {
    e.preventDefault();
    open({ source, defaultMessage });
  };
  if (asChild && children) {
    // Clone the single child element and inject onClick + a sensible default testid
    const child = Array.isArray(children) ? children[0] : children;
    return (
      <span onClick={onClick} {...rest}>
        {child}
      </span>
    );
  }
  return (
    <button onClick={onClick} {...rest}>
      {children}
    </button>
  );
}
